'use client'

import { cn } from '../lib/utils'

export interface ViewToggleOption<T extends string> {
  value: T
  label: string
}

export interface ViewToggleProps<T extends string> {
  options: ReadonlyArray<ViewToggleOption<T>>
  value: T
  onChange: (next: T) => void
  ariaLabel?: string
  className?: string
  /** Compact pill for dense toolbars (smaller height, padding, type). Ignored by `line`. */
  size?: 'default' | 'sm'
  /** `line`: underlined text tabs, for a second level of navigation under NavTabs. */
  variant?: 'pill' | 'line'
}

/**
 * Segmented pill toggle for switching between view modes (e.g. list/grid).
 * Matches the admin/NavTabs treatment: bg-muted pill, h-7 inner buttons,
 * violet active state. Announces itself as a group and uses aria-pressed
 * on each button so screen readers report the toggle state.
 */
export function ViewToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel = 'View mode',
  className,
  size = 'default',
  variant = 'pill',
}: ViewToggleProps<T>) {
  if (variant === 'line') {
    return (
      <div role="group" aria-label={ariaLabel} className={cn('flex items-center gap-5', className)}>
        {options.map((opt) => {
          const active = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                '-mb-px cursor-pointer border-b-2 pb-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--l3-violet)]/50',
                active
                  ? 'border-[var(--l3-violet)] text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    )
  }
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg bg-muted/60 p-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'cursor-pointer rounded-md font-medium capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--l3-violet)]/50',
              size === 'sm' ? 'h-6 px-2.5 text-[0.7rem]' : 'h-7 px-3 text-[0.8rem]',
              active
                ? 'bg-background text-[var(--l3-violet)] shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
